import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getAdminBlogs,
    deleteBlog,
    getCategories,
    createCategory,
    deleteCategory,
    getAuthors,
    createAuthor,
    deleteAuthor,
    authAPI,
    togglePublishBlog,
    getSubscribers,
    deleteSubscriber,
    BLOG_API_BASE_URL,
} from "../../../hooks/remote/blogApi";
import { extractErrorMessage } from "../../../hooks/constants";
import { toast } from "react-toastify";

const AdminDashboard = () => {
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all");

    // Modals
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showAuthorModal, setShowAuthorModal] = useState(false);
    const [showSubscriberModal, setShowSubscriberModal] = useState(false);
    const [categoryName, setCategoryName] = useState("");
    const [categoryDesc, setCategoryDesc] = useState("");
    const [authorName, setAuthorName] = useState("");
    const [authorBio, setAuthorBio] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [blogsData, categoriesData, authorsData, subscribersData] =
                await Promise.all([
                    getAdminBlogs(),
                    getCategories(),
                    getAuthors(),
                    getSubscribers(),
                ]);
            setBlogs(Array.isArray(blogsData) ? blogsData : []);
            setCategories(Array.isArray(categoriesData) ? categoriesData : []);
            setAuthors(Array.isArray(authorsData) ? authorsData : []);
            setSubscribers(Array.isArray(subscribersData) ? subscribersData : []);
        } catch (err) {
            console.error("Fetch data error:", err);
            const errorMsg = extractErrorMessage(err);
            setError(errorMsg);
            toast.error(errorMsg, { theme: "colored" });

            if (
                errorMsg.toLowerCase().includes("token") ||
                errorMsg.toLowerCase().includes("unauthorized")
            ) {
                authAPI.logout();
                setTimeout(() => navigate("/blog-admin/login"), 100);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

        try {
            const result = await deleteBlog(id);
            if (result.success) {
                setBlogs((prev) => prev.filter((b) => b.id !== id));
                toast.success("Blog deleted successfully!", { theme: "colored" });
                try {
                    localStorage.setItem("blogChanged", Date.now().toString());
                    window.dispatchEvent(new Event("blogChanged"));
                } catch { }
                fetchData();
            } else {
                const errorMsg = result.message || "Failed to delete blog";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleTogglePublish = async (id) => {
        try {
            const result = await togglePublishBlog(id);
            if (result.success) {
                setBlogs((prev) =>
                    prev.map((b) =>
                        b.id === id
                            ? {
                                ...b,
                                published: !b.published,
                                publishedAt: result.data?.publishedAt,
                            }
                            : b
                    )
                );
                try {
                    localStorage.setItem("blogChanged", Date.now().toString());
                    window.dispatchEvent(new Event("blogChanged"));
                } catch { }
                fetchData();
                toast.success("Publish status updated!", { theme: "colored" });
            } else {
                const errorMsg = result.message || "Failed to update publish status";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            const result = await createCategory({
                name: categoryName,
                description: categoryDesc,
            });
            if (result.success) {
                toast.success("Category created successfully!", { theme: "colored" });
                setCategoryName("");
                setCategoryDesc("");
                fetchData();
            } else {
                const errorMsg = result.message || "Failed to create category";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleDeleteCategory = async (id, name) => {
        if (!window.confirm(`Delete category "${name}"?`)) return;
        try {
            const result = await deleteCategory(id);
            if (result.success) {
                toast.success("Category deleted!", { theme: "colored" });
                fetchData();
            } else {
                const errorMsg = result.message || "Failed to delete category";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleCreateAuthor = async (e) => {
        e.preventDefault();
        try {
            const result = await createAuthor({ name: authorName, bio: authorBio });
            if (result.success) {
                toast.success("Author created successfully!", { theme: "colored" });
                setAuthorName("");
                setAuthorBio("");
                setShowAuthorModal(false);
                fetchData();
            } else {
                const errorMsg = result.message || "Failed to create author";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleDeleteAuthor = async (id, name) => {
        if (!window.confirm(`Delete author "${name}"?`)) return;
        try {
            const result = await deleteAuthor(id);
            if (result.success) {
                toast.success("Author deleted!", { theme: "colored" });
                fetchData();
            } else {
                const errorMsg = result.message || "Failed to delete author";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleDeleteSubscriber = async (id, email) => {
        if (!window.confirm(`Remove subscriber ${email}?`)) return;
        try {
            const result = await deleteSubscriber(id);
            if (result.success) {
                setSubscribers((prev) => prev.filter((sub) => sub.id !== id));
                toast.success("Subscriber removed", { theme: "colored" });
            } else {
                const errorMsg = result.message || "Failed to remove subscriber";
                toast.error(errorMsg, { theme: "colored" });
            }
        } catch (err) {
            const errorMsg = extractErrorMessage(err);
            toast.error(errorMsg, { theme: "colored" });
        }
    };

    const handleLogout = () => {
        if (window.confirm("Are you sure you want to logout?")) {
            authAPI.logout();
            navigate("/blog-admin/login");
        }
    };

    const filteredBlogs = blogs.filter((blog) => {
        if (filter === "published") return blog.published;
        if (filter === "draft") return !blog.published;
        return true;
    });

    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith("http")) return imagePath;
        const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
        return `${BLOG_API_BASE_URL.replace("/api", "")}${cleanPath}`;
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-[#141D26] px-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primaryBlue mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-300">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#141D26] py-4 md:py-8">
            <div className="max-w-6xl mx-auto px-3 md:px-4">
                {/* Header */}
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-4 md:p-6 mb-4 md:mb-6">
                    <div className="flex flex-col gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                                Blog Admin Dashboard
                            </h1>
                            <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">
                                Manage your blog posts
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => navigate("/blog-admin/create-blog")}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center gap-2 text-sm md:text-base"
                            >
                                ➕ New Blog
                            </button>
                            <button
                                onClick={() => setShowCategoryModal(true)}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition text-sm md:text-base"
                            >
                                Add Category
                            </button>
                            <button
                                onClick={() => setShowAuthorModal(true)}
                                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition text-sm md:text-base"
                            >
                                Add Author
                            </button>
                            <button
                                onClick={() => setShowSubscriberModal(true)}
                                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition text-sm md:text-base"
                            >
                                Subscribers
                            </button>
                            <button
                                onClick={handleLogout}
                                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition text-sm md:text-base"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-3 md:p-6">
                        <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Total</p>
                        <p className="text-xl md:text-3xl font-bold text-gray-800 dark:text-white">
                            {blogs.length}
                        </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-3 md:p-6">
                        <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Published</p>
                        <p className="text-xl md:text-3xl font-bold text-green-600">
                            {blogs.filter((b) => b.published).length}
                        </p>
                    </div>
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-3 md:p-6">
                        <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Drafts</p>
                        <p className="text-xl md:text-3xl font-bold text-orange-600">
                            {blogs.filter((b) => !b.published).length}
                        </p>
                    </div>
                </div>

                {/* Filter */}
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-3 md:p-4 mb-4 md:mb-6 overflow-x-auto">
                    <div className="flex gap-2 min-w-max">
                        <button
                            onClick={() => setFilter("all")}
                            className={`px-3 md:px-4 py-2 rounded-lg font-medium transition text-xs md:text-sm ${filter === "all"
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                }`}
                        >
                            All ({blogs.length})
                        </button>
                        <button
                            onClick={() => setFilter("published")}
                            className={`px-3 md:px-4 py-2 rounded-lg font-medium transition text-xs md:text-sm ${filter === "published"
                                    ? "bg-green-600 text-white"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                }`}
                        >
                            Published ({blogs.filter((b) => b.published).length})
                        </button>
                        <button
                            onClick={() => setFilter("draft")}
                            className={`px-3 md:px-4 py-2 rounded-lg font-medium transition text-xs md:text-sm ${filter === "draft"
                                    ? "bg-orange-600 text-white"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                }`}
                        >
                            Drafts ({blogs.filter((b) => !b.published).length})
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-3 md:px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Blogs List */}
                {filteredBlogs.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-12 text-center">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
                            No blogs found
                        </h3>
                        <button
                            onClick={() => navigate("/blog-admin/create-blog")}
                            className="bg-blue-600 dark:bg-primaryBlue text-white px-6 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-primaryBlue/90 transition"
                        >
                            Create Blog
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-3 md:gap-4">
                        {filteredBlogs.map((blog) => (
                            <div
                                key={blog.id}
                                className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-4 hover:shadow-lg transition"
                            >
                                <div className="flex flex-col gap-3">
                                    {blog.featuredImage && (
                                        <img
                                            src={getImageUrl(blog.featuredImage)}
                                            alt={blog.title}
                                            className="w-full h-40 md:h-48 object-cover rounded-lg"
                                            onError={(e) => {
                                                e.target.src = "https://via.placeholder.com/400x200?text=No+Image";
                                            }}
                                        />
                                    )}

                                    <div>
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                                                {blog.title}
                                            </h2>
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs font-semibold ${blog.published
                                                        ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400"
                                                        : "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400"
                                                    }`}
                                            >
                                                {blog.published ? "Published" : "Draft"}
                                            </span>
                                        </div>

                                        <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400 mb-3">
                                            {blog.Category && (
                                                <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                                    {blog.Category.name}
                                                </span>
                                            )}
                                            {blog.Author && <span>✍️ {blog.Author.name}</span>}
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={() => navigate(`/blog-admin/blogs/${blog.id}`)}
                                                className="bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition text-xs"
                                            >
                                                View
                                            </button>
                                            <button
                                                onClick={() => navigate(`/blog-admin/edit-blog/${blog.id}`)}
                                                className="bg-blue-500 dark:bg-primaryBlue text-white px-3 py-2 rounded-lg hover:bg-blue-600 dark:hover:bg-primaryBlue/90 transition text-xs"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleTogglePublish(blog.id)}
                                                className={`bg-amber-500 text-white px-3 py-2 rounded-lg hover:bg-amber-600 transition text-xs`}
                                            >
                                                {blog.published ? "Unpublish" : "Publish"}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(blog.id, blog.title)}
                                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition text-xs"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Category Modal */}
            {showCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Manage Categories</h2>
                        <form onSubmit={handleCreateCategory} className="mb-6">
                            <input
                                type="text"
                                placeholder="Category Name"
                                className="w-full p-3 border dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded mb-3"
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                required
                            />
                            <button
                                type="submit"
                                className="w-full bg-purple-600 text-white p-3 rounded hover:bg-purple-700"
                            >
                                Add Category
                            </button>
                        </form>
                        <div className="border-t dark:border-gray-700 pt-4">
                            <h3 className="font-semibold mb-3 text-gray-800 dark:text-white">
                                Existing Categories ({categories.length})
                            </h3>
                            {categories.map((cat) => (
                                <div
                                    key={cat.id}
                                    className="flex justify-between items-center p-2 border-b dark:border-gray-700"
                                >
                                    <span className="text-gray-800 dark:text-white">{cat.name}</span>
                                    <button
                                        onClick={() => handleDeleteCategory(cat.id, cat.name)}
                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowCategoryModal(false)}
                            className="w-full mt-4 bg-gray-600 text-white p-3 rounded hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Author Modal */}
            {showAuthorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Manage Authors</h2>
                        <form onSubmit={handleCreateAuthor} className="mb-6">
                            <input
                                type="text"
                                placeholder="Author Name"
                                className="w-full p-3 border dark:border-gray-600 dark:bg-slate-700 dark:text-white rounded mb-3"
                                value={authorName}
                                onChange={(e) => setAuthorName(e.target.value)}
                                required
                            />
                            <button
                                type="submit"
                                className="w-full bg-indigo-600 text-white p-3 rounded hover:bg-indigo-700"
                            >
                                Add Author
                            </button>
                        </form>
                        <div className="border-t dark:border-gray-700 pt-4">
                            <h3 className="font-semibold mb-3 text-gray-800 dark:text-white">
                                Existing Authors ({authors.length})
                            </h3>
                            {authors.map((author) => (
                                <div
                                    key={author.id}
                                    className="flex justify-between items-center p-2 border-b dark:border-gray-700"
                                >
                                    <span className="text-gray-800 dark:text-white">{author.name}</span>
                                    <button
                                        onClick={() => handleDeleteAuthor(author.id, author.name)}
                                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                    >
                                        Delete
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => setShowAuthorModal(false)}
                            className="w-full mt-4 bg-gray-600 text-white p-3 rounded hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showSubscriberModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-white">Subscribers</h2>
                        {subscribers.length === 0 ? (
                            <p className="text-sm text-gray-600 dark:text-gray-300">No subscribers yet.</p>
                        ) : (
                            <ul className="divide-y dark:divide-gray-700">
                                {subscribers.map((subscriber) => (
                                    <li
                                        key={subscriber.id}
                                        className="flex items-center justify-between py-3"
                                    >
                                        <div>
                                            <p className="font-medium text-gray-800 dark:text-white">
                                                {subscriber.email}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {new Date(subscriber.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() =>
                                                handleDeleteSubscriber(subscriber.id, subscriber.email)
                                            }
                                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                                        >
                                            Delete
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button
                            onClick={() => setShowSubscriberModal(false)}
                            className="w-full mt-4 bg-gray-600 text-white p-3 rounded hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;

